import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useCurrency } from '../hooks/useCurrency';
import BookingForm from './BookingForm';

interface HotelRoom {
    id: string;
    name: string;
    type: string;
    size: string;
    maxGuests: number;
    price: number;
    images: string[];
    amenities: string[];
    description: string;
}

interface HotelReview {
    id: string;
    userName: string;
    userAvatar: string;
    rating: number;
    date: string;
    title: string;
    comment: string;
    helpful: number;
}

const HotelDetails: React.FC = () => {
    const { t } = useTranslation();
    const { formatPrice } = useCurrency();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    
    const [hotel, setHotel] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(0);
    const [activeTab, setActiveTab] = useState('overview');

    // Mock hotel data
    const mockHotel = {
        id: id || '1',
        name: 'Grand Palace Hotel',
        location: 'Istanbul, Turkey',
        address: 'Sultanahmet Square, Fatih, Istanbul',
        rating: 4.8,
        reviewCount: 1247,
        priceFrom: 250,
        description: 'Experience luxury and comfort at the Grand Palace Hotel, located in the heart of historic Istanbul. Our hotel offers breathtaking views of the Blue Mosque and Hagia Sophia, combining traditional Turkish hospitality with modern amenities.',
        images: [
            'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800',
            'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800',
            'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800',
            'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800',
            'https://images.unsplash.com/photo-1595576508898-0ad5c879a061?w=800'
        ],
        amenities: [
            { id: '1', name: 'Free WiFi', icon: '📶', category: 'general' },
            { id: '2', name: 'Swimming Pool', icon: '🏊‍♂️', category: 'wellness' },
            { id: '3', name: 'Spa & Wellness', icon: '🧘‍♀️', category: 'wellness' },
            { id: '4', name: 'Restaurant', icon: '🍽️', category: 'dining' },
            { id: '5', name: 'Fitness Center', icon: '💪', category: 'wellness' },
            { id: '6', name: 'Business Center', icon: '💼', category: 'business' },
            { id: '7', name: 'Concierge', icon: '🎩', category: 'general' },
            { id: '8', name: 'Room Service', icon: '🛎️', category: 'room' },
            { id: '9', name: 'Airport Shuttle', icon: '🚐', category: 'general' },
            { id: '10', name: 'Parking', icon: '🅿️', category: 'general' }
        ],
        rooms: [
            {
                id: '1',
                name: 'Deluxe Room',
                type: 'Standard',
                size: '35 sqm',
                maxGuests: 2,
                price: 250,
                images: ['https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=400'],
                amenities: ['King Bed', 'City View', 'Mini Bar', 'Air Conditioning'],
                description: 'Spacious room with modern amenities and city views'
            },
            {
                id: '2',
                name: 'Executive Suite',
                type: 'Suite',
                size: '65 sqm',
                maxGuests: 4,
                price: 450,
                images: ['https://images.unsplash.com/photo-1618773928121-c32242e63f39?w=400'],
                amenities: ['King Bed', 'Separate Living Area', 'Balcony', 'Premium Amenities'],
                description: 'Luxurious suite with separate living area and premium amenities'
            },
            {
                id: '3',
                name: 'Presidential Suite',
                type: 'Suite',
                size: '120 sqm',
                maxGuests: 6,
                price: 850,
                images: ['https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?w=400'],
                amenities: ['King Bed', 'Private Terrace', 'Butler Service', 'Premium Views'],
                description: 'Ultimate luxury with private terrace and butler service'
            }
        ],
        reviews: [
            {
                id: '1',
                userName: 'Ahmed Al-Rashid',
                userAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100',
                rating: 5,
                date: '2024-01-15',
                title: 'Amazing stay in Istanbul!',
                comment: 'The hotel exceeded all our expectations. The location is perfect, the staff is incredibly helpful, and the rooms are beautiful. The view of the Blue Mosque from our room was breathtaking.',
                helpful: 23
            },
            {
                id: '2',
                userName: 'Sarah Johnson',
                userAvatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100',
                rating: 4,
                date: '2024-01-10',
                title: 'Great location and service',
                comment: 'Perfect location for exploring Istanbul. The hotel staff was very accommodating and the breakfast was excellent. The only minor issue was the WiFi speed in our room.',
                helpful: 18
            },
            {
                id: '3',
                userName: 'Mehmet Yılmaz',
                userAvatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100',
                rating: 5,
                date: '2024-01-08',
                title: 'Perfect for business and leisure',
                comment: 'Stayed here for a business trip and was impressed by the business facilities. The spa was also a great way to relax after meetings. Highly recommended!',
                helpful: 15
            }
        ],
        policies: {
            checkIn: '15:00',
            checkOut: '12:00',
            cancellation: 'Free cancellation up to 24 hours before check-in',
            pets: 'Pets allowed with additional fee',
            smoking: 'Non-smoking rooms available'
        }
    };

    useEffect(() => {
        const fetchHotel = async () => {
            setLoading(true);
            try {
                await new Promise(resolve => setTimeout(resolve, 1000));
                setHotel(mockHotel);
            } catch (error) {
                console.error('Error fetching hotel:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchHotel();
    }, [id]);

    if (loading) {
        return (
            <div className="hotel-details-loading">
                <div className="loading-spinner"></div>
                <p>{t('hotelDetails.loading')}</p>
            </div>
        );
    }

    if (!hotel) {
        return (
            <div className="hotel-details-error">
                <h2>{t('hotelDetails.notFound')}</h2>
                <p>{t('hotelDetails.notFoundDesc')}</p>
                <Link to="/hotels" className="btn btn-primary">{t('hotelDetails.backToHotels')}</Link>
            </div>
        );
    }

    const renderStars = (rating: number) => {
        return '⭐'.repeat(Math.floor(rating));
    };

    const getAmenityCategory = (category: string) => {
        return t(`hotelDetails.amenityCategories.${category}`) || 'Other';
    };

    const groupedAmenities = hotel.amenities.reduce((acc: any, amenity: any) => {
        if (!acc[amenity.category]) {
            acc[amenity.category] = [];
        }
        acc[amenity.category].push(amenity);
        return acc;
    }, {});

    return (
        <div className="hotel-details">
            {/* Header */}
            <div className="hotel-header">
                <div className="container">
                    <motion.div 
                        className="hotel-breadcrumb"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <Link to="/hotels">{t('header.hotels')}</Link> / <span>{hotel.name}</span>
                    </motion.div>
                    
                    <motion.div 
                        className="hotel-title-section"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        <h1>{hotel.name}</h1>
                        <div className="hotel-location">
                            <span className="location-icon">📍</span>
                            <span>{hotel.location}</span>
                        </div>
                        <div className="hotel-rating">
                            <span className="rating-stars">{renderStars(hotel.rating)}</span>
                            <span className="rating-number">{hotel.rating}</span>
                            <span className="rating-count">({hotel.reviewCount} reviews)</span>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Image Gallery */}
            <div className="hotel-gallery">
                <div className="container">
                    <motion.div 
                        className="gallery-main"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2 }}
                    >
                        <img 
                            src={hotel.images[selectedImage]} 
                            alt={hotel.name}
                            className="main-image"
                        />
                        <div className="gallery-thumbnails">
                            {hotel.images.map((image: string, index: number) => (
                                <img
                                    key={index}
                                    src={image}
                                    alt={`${hotel.name} ${index + 1}`}
                                    className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                                    onClick={() => setSelectedImage(index)}
                                />
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container">
                <div className="hotel-content">
                    <div className="hotel-main">
                        {/* Tabs */}
                        <div className="hotel-tabs">
                            <button 
                                className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                                onClick={() => setActiveTab('overview')}
                            >
                                {t('hotelDetails.overview')}
                            </button>
                            <button 
                                className={`tab ${activeTab === 'rooms' ? 'active' : ''}`}
                                onClick={() => setActiveTab('rooms')}
                            >
                                {t('hotelDetails.rooms')}
                            </button>
                            <button 
                                className={`tab ${activeTab === 'amenities' ? 'active' : ''}`}
                                onClick={() => setActiveTab('amenities')}
                            >
                                {t('hotelDetails.amenities')}
                            </button>
                            <button 
                                className={`tab ${activeTab === 'reviews' ? 'active' : ''}`}
                                onClick={() => setActiveTab('reviews')}
                            >
                                {t('hotelDetails.reviews')}
                            </button>
                        </div>

                        {/* Tab Content */}
                        <div className="tab-content">
                            {activeTab === 'overview' && (
                                <motion.div 
                                    className="overview-content"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                >
                                    <h3>{t('hotelDetails.aboutHotel')}</h3>
                                    <p>{hotel.description}</p>
                                    
                                    <div className="hotel-highlights">
                                        <h4>{t('hotelDetails.whatMakesSpecial')}</h4>
                                        <ul>
                                            <li>{t('hotelDetails.highlight1')}</li>
                                            <li>{t('hotelDetails.highlight2')}</li>
                                            <li>{t('hotelDetails.highlight3')}</li>
                                            <li>{t('hotelDetails.highlight4')}</li>
                                            <li>{t('hotelDetails.highlight5')}</li>
                                        </ul>
                                    </div>

                                    <div className="hotel-policies">
                                        <h4>{t('hotelDetails.hotelPolicies')}</h4>
                                        <div className="policies-grid">
                                            <div className="policy-item">
                                                <strong>{t('hotelDetails.checkIn')}:</strong> {hotel.policies.checkIn}
                                            </div>
                                            <div className="policy-item">
                                                <strong>{t('hotelDetails.checkOut')}:</strong> {hotel.policies.checkOut}
                                            </div>
                                            <div className="policy-item">
                                                <strong>{t('hotelDetails.cancellation')}:</strong> {hotel.policies.cancellation}
                                            </div>
                                            <div className="policy-item">
                                                <strong>{t('hotelDetails.pets')}:</strong> {hotel.policies.pets}
                                            </div>
                                            <div className="policy-item">
                                                <strong>{t('hotelDetails.smoking')}:</strong> {hotel.policies.smoking}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {activeTab === 'rooms' && (
                                <motion.div 
                                    className="rooms-content"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                >
                                    <h3>{t('hotelDetails.availableRooms')}</h3>
                                    {hotel.rooms.map((room: HotelRoom) => (
                                        <div key={room.id} className="room-card">
                                            <div className="room-image">
                                                <img src={room.images[0]} alt={room.name} />
                                            </div>
                                            <div className="room-details">
                                                <h4>{room.name}</h4>
                                                <p className="room-type">{room.type} • {room.size} • Up to {room.maxGuests} guests</p>
                                                <p className="room-description">{room.description}</p>
                                                <div className="room-amenities">
                                                    {room.amenities.map((amenity, index) => (
                                                        <span key={index} className="amenity-tag">{amenity}</span>
                                                    ))}
                                                </div>
                                                <div className="room-price">
                                                    <span className="price">{formatPrice(room.price)}</span>
                                                    <span className="price-period">{t('hotelDetails.perNight')}</span>
                                                </div>
                                                <button className="btn btn-primary">
                                                    {t('hotelDetails.selectRoom')}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </motion.div>
                            )}

                            {activeTab === 'amenities' && (
                                <motion.div 
                                    className="amenities-content"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                >
                                    <h3>{t('hotelDetails.hotelAmenities')}</h3>
                                    {Object.entries(groupedAmenities).map(([category, amenities]: [string, any]) => (
                                        <div key={category} className="amenity-category">
                                            <h4>{getAmenityCategory(category)}</h4>
                                            <div className="amenities-grid">
                                                {amenities.map((amenity: any) => (
                                                    <div key={amenity.id} className="amenity-item">
                                                        <span className="amenity-icon">{amenity.icon}</span>
                                                        <span className="amenity-name">{amenity.name}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </motion.div>
                            )}

                            {activeTab === 'reviews' && (
                                <motion.div 
                                    className="reviews-content"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                >
                                    <h3>{t('hotelDetails.guestReviews')}</h3>
                                    <div className="reviews-summary">
                                        <div className="rating-breakdown">
                                            <div className="rating-overall">
                                                <span className="rating-number">{hotel.rating}</span>
                                                <span className="rating-stars">{renderStars(hotel.rating)}</span>
                                                <span className="rating-count">{hotel.reviewCount} reviews</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="reviews-list">
                                        {hotel.reviews.map((review: HotelReview) => (
                                            <div key={review.id} className="review-card">
                                                <div className="review-header">
                                                    <img src={review.userAvatar} alt={review.userName} className="review-avatar" />
                                                    <div className="review-user">
                                                        <h5>{review.userName}</h5>
                                                        <div className="review-rating">
                                                            <span className="stars">{renderStars(review.rating)}</span>
                                                            <span className="date">{new Date(review.date).toLocaleDateString()}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <h6 className="review-title">{review.title}</h6>
                                                <p className="review-comment">{review.comment}</p>
                                                <div className="review-helpful">
                                                    <span>{t('hotelDetails.wasHelpful')}</span>
                                                    <button className="helpful-btn">👍 {review.helpful}</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="hotel-sidebar">
                        <motion.div 
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 }}
                        >
                            <BookingForm 
                                hotelId={hotel.id}
                                onBookingSuccess={(bookingId, confirmationNumber) => {
                                    console.log('Booking successful:', { bookingId, confirmationNumber });
                                }}
                            />
                        </motion.div>

                        <div className="hotel-contact">
                            <h4>{t('hotelDetails.contactHotel')}</h4>
                            <div className="contact-info">
                                <div className="contact-item">
                                    <span className="contact-icon">📞</span>
                                    <span>+90 212 123 4567</span>
                                </div>
                                <div className="contact-item">
                                    <span className="contact-icon">✉️</span>
                                    <span>info@grandpalace.com</span>
                                </div>
                                <div className="contact-item">
                                    <span className="contact-icon">📍</span>
                                    <span>{hotel.address}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HotelDetails;
